"use client";

import { useSession } from "next-auth/react";
import { useLocalStorage } from "@shared-utils/hooks/use-local-storage";
import { BarChart3, ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "./shadcn/button";
import { Sheet, SheetContent, SheetTitle } from "./shadcn/sheet";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { MessagesCountPerChatWithFiles } from "@shared-components/core-metrics/messages-count-per-chat-with-files";
import { Tab, TabGroup } from "@shared-components/tab-group";
import {
  ProfilesContent,
  AiModelsCountInChats,
  PluginUsageChart,
  ActiveUsersPerTool,
  PluginsUsage,
  SummaryChatsAndImages,
  ResearchProjectsByType,
  ResearchCost,
} from "@shared-components/core-metrics";
import { z } from "zod";
import { PromptCountInChats } from "@shared-components/core-metrics/prompt-count-in-chats";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import { DepartmentSelect } from "@shared-components/department-select";
import { DepartmentBadge } from "@shared-components/core-metrics/department-badge";
import {
  DepartmentStackProvider,
  useDepartmentStackContext,
} from "@shared-components/department-stack-provider";

const rangeSchema = z.enum(["all", "year", "month", "week"]);
type RangeType = z.infer<typeof rangeSchema>;

const rangesArray = rangeSchema.options;

const getRangeLabel = (range: RangeType): string => {
  return range;
};

const rangeOptions: Record<RangeType, GranularityType[]> = {
  all: ["day", "week", "month", "year"],
  year: ["day", "week", "month"],
  month: ["day", "week"],
  week: ["day"],
} as const;

const getGranularityOptions = (range: RangeType): GranularityType[] => {
  return rangeOptions[range] ?? rangeOptions["week"];
};

const getGranularityLabel = (granularity: GranularityType): string => {
  switch (granularity) {
    case "day":
      return "Daily";
    case "week":
      return "Weekly";
    case "month":
      return "Monthly";
    case "year":
      return "Yearly";
  }
};

export const CoreMetricsSheet = () => {
  const { data: session } = useSession();
  const isAdmin = session?.user.isAdmin ?? false;

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Date states
  const [range, setRange] = useLocalStorage<RangeType>(
    "core-metrics-range",
    "month",
  );
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const [granularity, setGranularity] = useLocalStorage<GranularityType>(
    "core-metrics-granularity",
    "day",
  );

  // Update state on range change or offset change
  useEffect(() => {
    const date = new Date();

    switch (range) {
      case "all": {
        // Use UTC to avoid timezone issues during serialization
        const allStart = new Date("2024-04-22T00:00:00.000Z");
        const allEnd = new Date(
          Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            23,
            59,
            59,
            999,
          ),
        );
        setStartDate(allStart);
        setEndDate(allEnd);
        break;
      }
      case "year": {
        date.setFullYear(date.getFullYear() + offset);
        // Use UTC constructor to avoid timezone conversion
        const yearStart = new Date(
          Date.UTC(date.getFullYear(), 0, 1, 0, 0, 0, 0),
        );
        const yearEnd = new Date(
          Date.UTC(date.getFullYear() + 1, 0, 0, 23, 59, 59, 999),
        );
        setStartDate(yearStart);
        setEndDate(yearEnd);
        break;
      }
      case "month": {
        date.setMonth(date.getMonth() + offset);
        // Use UTC constructor to avoid timezone conversion
        const monthStart = new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0),
        );
        const monthEnd = new Date(
          Date.UTC(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999),
        );
        setStartDate(monthStart);
        setEndDate(monthEnd);
        break;
      }
      case "week": {
        const today = new Date();
        today.setDate(today.getDate() + offset * 7);

        const dayOfWeek = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));

        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        // Use UTC to avoid timezone conversion
        const weekStart = new Date(
          Date.UTC(
            monday.getFullYear(),
            monday.getMonth(),
            monday.getDate(),
            0,
            0,
            0,
            0,
          ),
        );
        const weekEnd = new Date(
          Date.UTC(
            sunday.getFullYear(),
            sunday.getMonth(),
            sunday.getDate(),
            23,
            59,
            59,
            999,
          ),
        );

        setStartDate(weekStart);
        setEndDate(weekEnd);
        break;
      }
    }
  }, [offset, range]);

  useEffect(() => {
    setOffset(0);
  }, [range]);

  useEffect(() => {
    if (range === "week" && granularity !== "day") {
      setGranularity("day");
      return;
    }
    const options = getGranularityOptions(range);
    if (!options.includes(granularity) && options[0]) {
      setGranularity(options[0]);
    }
  }, [granularity, range, setGranularity]);

  if (!isAdmin) {
    return null;
  }

  return (
    <>
      {isAdmin && (
        <Button
          className="px-2 hidden md:block"
          onClick={() => setIsOpen(true)}
          variant="ghost"
        >
          <BarChart3 />
        </Button>
      )}
      <Sheet open={isOpen} onOpenChange={(value) => setIsOpen(value)}>
        <SheetContent className="bg-panel-200">
          <DepartmentStackProvider>
            <VisuallyHidden.Root>
              <SheetTitle>{"Core Metrics"}</SheetTitle>
            </VisuallyHidden.Root>
            <div className="mt-4 flex justify-between">
              <div className="flex flex-col gap-2 align-bottom">
                <h1 className="flex-none">{"Core Metrics"}</h1>
                <div className="flex flex-col gap-2">
                  <ParentStateNavigator />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span>
                    {`${startDate?.toLocaleDateString()}-${endDate?.toLocaleDateString()}`}
                  </span>
                  <Button
                    variant={"secondary"}
                    onClick={() => setOffset(offset - 1)}
                  >
                    <ChevronLeft />
                  </Button>
                  <Button
                    variant={"secondary"}
                    onClick={() => setOffset(offset + 1)}
                  >
                    <ChevronRight />
                  </Button>
                  {rangesArray.map((rangeValue, index) => (
                    <Button
                      key={index}
                      variant={range === rangeValue ? "default" : "secondary"}
                      onClick={() => setRange(rangeValue)}
                    >
                      {getRangeLabel(rangeValue)}
                    </Button>
                  ))}
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  {getGranularityOptions(range).map((g) => (
                    <Button
                      key={g}
                      variant={granularity === g ? "default" : "secondary"}
                      onClick={() => setGranularity(g)}
                    >
                      {getGranularityLabel(g)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <div className="-ml-6 mt-4 flex w-full flex-col gap-6">
              {startDate && endDate && (
                <TabGroup
                  name="admin-feature-flags-tabs"
                  defaultValue="Summary"
                >
                  <Tab
                    key={0}
                    label={"Summary"}
                    className="-mr-12 max-h-[100vh] overflow-y-auto pb-85 pl-6 pr-6 pt-4"
                  >
                    <SummaryChatsAndImages
                      startDate={startDate}
                      endDate={endDate}
                      granularity={granularity}
                    />
                  </Tab>
                  <Tab
                    key={1}
                    label={"Chat"}
                    className="-mr-12 pb-72 pl-6 pt-4"
                  >
                    <div className="max-h-screen overflow-y-auto pb-40 pr-6">
                      <MessagesCountPerChatWithFiles
                        startDate={startDate}
                        endDate={endDate}
                      />
                      <AiModelsCountInChats
                        startDate={startDate}
                        endDate={endDate}
                        granularity={granularity}
                      />
                      <PluginUsageChart
                        startDate={startDate}
                        endDate={endDate}
                        granularity={granularity}
                      />
                      <PromptCountInChats
                        startDate={startDate}
                        endDate={endDate}
                      ></PromptCountInChats>
                    </div>
                  </Tab>
                  <Tab
                    key={2}
                    label={"Active Users"}
                    className="-mr-12 max-h-[100vh] overflow-y-auto pb-85 pl-6 pr-6 pt-4"
                  >
                    <ActiveUsersPerTool
                      startDate={startDate}
                      endDate={endDate}
                      granularity={granularity}
                    />
                  </Tab>
                  <Tab
                    key={3}
                    label={"Profiles Content"}
                    className="-mr-12 h-[100vh] pl-6 pr-6 pt-4"
                  >
                    <ProfilesContent
                      startDate={startDate}
                      endDate={endDate}
                      granularity={granularity}
                    />
                  </Tab>
                  <Tab
                    key={4}
                    label={"Plugins Usage"}
                    className="-mr-12 max-h-[100vh] overflow-y-auto pb-72 pl-6 pr-6 pt-4"
                  >
                    <PluginsUsage startDate={startDate} endDate={endDate} />
                  </Tab>
                  <Tab
                    key={5}
                    label={"Research"}
                    className="-mr-12 max-h-[100vh] overflow-y-auto pb-72 pl-6 pr-6 pt-4"
                  >
                    <ResearchProjectsByType
                      startDate={startDate}
                      endDate={endDate}
                      granularity={granularity}
                    />
                    <ResearchCost
                      startDate={startDate}
                      endDate={endDate}
                      granularity={granularity}
                    />
                  </Tab>
                </TabGroup>
              )}
            </div>
          </DepartmentStackProvider>
        </SheetContent>
      </Sheet>
    </>
  );
};

const ParentStateNavigator = () => {
  const {
    parentDepartmentId,
    pushParentDepartment,
    popParentDepartment,
    resetParentDepartment,
    selectedDepartmentName,
    setSelectedDepartmentName,
  } = useDepartmentStackContext();

  return (
    <>
      <div className="flex gap-2">
        <Button
          onClick={popParentDepartment}
          disabled={!parentDepartmentId}
          className="mb-4"
          size={"xs"}
        >
          {"Go back to parent department"}
        </Button>
        <Button
          size={"xs"}
          onClick={resetParentDepartment}
          disabled={!parentDepartmentId}
          className="mb-4"
        >
          {"Reset to top level"}
        </Button>
      </div>
      <div className="flex gap-2 items-end">
        <DepartmentSelect
          onSelect={(department) => {
            resetParentDepartment();
            setSelectedDepartmentName(department.name);
            pushParentDepartment(department.id);
          }}
        >
          {selectedDepartmentName !== null && (
            <DepartmentBadge
              onClick={() => {
                resetParentDepartment();
              }}
            >
              {selectedDepartmentName}
            </DepartmentBadge>
          )}
        </DepartmentSelect>
      </div>
    </>
  );
};
